/**
 * Shared `<PhoneInput>` - the canonical phone-entry surface for the
 * app. Wraps `react-phone-number-input` to get a country flag/selector
 * + libphonenumber-js-backed validation, and pairs it with an optional
 * extension field.
 *
 * Stored value is E.164 (`+14155550123`) with an optional RFC 3966
 * extension suffix (`+14155550123;ext=99`). Helpers for combining /
 * splitting that shape live in `@/shared/lib/forms/phone`.
 *
 * The default country comes from `FormattingContext.defaultCountryCode`
 * (seeded from the organisation's preferences) - callers can still
 * override it with the `defaultCountry` prop.
 */

import { forwardRef, useMemo } from 'react'
import type { ComponentProps } from 'react'
import LibPhoneInput, {
  type Country as LibCountry,
} from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js'

import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { cn } from '@/shared/lib/utils'
import { useFormattingContext } from '@/shared/lib/formatting/use-formatting-context'
import {
  combinePhoneValue,
  splitStoredValue,
} from '@/shared/lib/forms/phone'

type LibPhoneInputProps = ComponentProps<typeof LibPhoneInput>

export interface PhoneInputProps {
  /** Canonical stored value (E.164 + optional `;ext=<digits>`). */
  value: string
  /** Fired when either the base number or the extension changes. */
  onChange: (value: string) => void
  onBlur?: () => void
  /** ISO 3166-1 alpha-2 country code. Defaults to FormattingContext. */
  defaultCountry?: string
  placeholder?: string
  extensionPlaceholder?: string
  disabled?: boolean
  hasError?: boolean
  id?: string
  /** Extra className on the outer wrapper. */
  className?: string
  /** Hide the extension input (for forms that don't support extensions). */
  hideExtension?: boolean
}

/**
 * `react-phone-number-input` renders a <input type="tel"> internally
 * and spreads any `numberInputProps` onto it. We wrap the shared `Input`
 * so it picks up the same look/feel as the rest of the form system.
 */
const StyledNumberInput = forwardRef<HTMLInputElement, Record<string, unknown>>(
  function StyledNumberInput(props, ref) {
    return <Input ref={ref} {...(props as ComponentProps<typeof Input>)} />
  },
)

type CountrySelectOption = {
  value?: LibCountry
  label: string
}

type CountrySelectProps = {
  value?: LibCountry
  onChange: (value?: LibCountry) => void
  options?: CountrySelectOption[]
  disabled?: boolean
  className?: string
}

const INTERNATIONAL_OPTION_VALUE = '__international__'

function CountryPrefixSelect({
  value,
  onChange,
  options = [],
  disabled,
  className,
}: CountrySelectProps) {
  const selectValue = value ?? INTERNATIONAL_OPTION_VALUE

  return (
    <div className={cn('shrink-0', className)}>
      <Select
        value={selectValue}
        onValueChange={(next) => {
          onChange(next === INTERNATIONAL_OPTION_VALUE ? undefined : (next as LibCountry))
        }}
      >
        <SelectTrigger
          size="sm"
          disabled={disabled}
          className="w-[108px] shrink-0 rounded-field text-[13px]"
          aria-label="Country calling code"
        >
          <SelectValue placeholder="Country" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => {
            const optionValue = option.value ?? INTERNATIONAL_OPTION_VALUE
            const optionLabel = option.value
              ? `${option.value} (+${getCountryCallingCode(option.value)})`
              : option.label

            return (
              <SelectItem key={optionValue} value={optionValue}>
                {optionLabel}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
    </div>
  )
}

export function PhoneInput({
  value,
  onChange,
  onBlur,
  defaultCountry,
  placeholder,
  extensionPlaceholder = 'Ext.',
  disabled,
  hasError,
  id,
  className,
  hideExtension = false,
}: PhoneInputProps) {
  const { defaultCountryCode } = useFormattingContext()
  const resolvedDefault = (defaultCountry ?? defaultCountryCode) as
    | LibCountry
    | undefined

  const { base, extension } = splitStoredValue(value)

  // `react-phone-number-input` requires an E.164 `value` and otherwise logs an
  // error, flips the country selector to "International", and renders the digits
  // unformatted. Stored/auto-filled data can arrive in national format (e.g. a
  // partnership's "0424 159 917"), so defensively normalise to E.164 using the
  // resolved default country before handing it over. Display-only - the parent's
  // stored value is untouched until the user edits the field.
  const normalizedBase = useMemo(() => {
    if (!base) return undefined
    if (base.startsWith('+')) return base
    try {
      const parsed = parsePhoneNumberFromString(base, resolvedDefault)
      return parsed?.number ?? base
    } catch {
      return base
    }
  }, [base, resolvedDefault])

  const handleBaseChange: LibPhoneInputProps['onChange'] = (nextBase) => {
    onChange(combinePhoneValue(nextBase ?? '', extension))
  }

  const handleExtensionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value.replace(/\D/g, '')
    onChange(combinePhoneValue(base, next))
  }

  return (
    <div className={cn('flex items-stretch gap-2', className)}>
      <LibPhoneInput
        id={id}
        value={normalizedBase}
        onChange={handleBaseChange}
        onBlur={onBlur}
        defaultCountry={resolvedDefault}
        countrySelectComponent={CountryPrefixSelect}
        countryCallingCodeEditable={false}
        disabled={disabled}
        placeholder={placeholder}
        inputComponent={StyledNumberInput}
        numberInputProps={{
          'aria-invalid': hasError || undefined,
          className: hasError ? 'border-destructive' : undefined,
        }}
        className={cn(
          'sx-phone-input flex-1 items-center gap-2',
          hasError && 'sx-phone-input--error',
        )}
      />
      {!hideExtension ? (
        <Input
          type="text"
          inputMode="numeric"
          value={extension}
          onChange={handleExtensionChange}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={extensionPlaceholder}
          aria-label="Phone extension"
          aria-invalid={hasError || undefined}
          className={cn('w-20 shrink-0', hasError && 'border-destructive')}
        />
      ) : null}
    </div>
  )
}
